import React, { useState, useRef, useEffect } from "react";
import {
  Send,
  Loader2,
  FileText,
  AlertCircle,
  CheckCircle2,
  Link,
  Info,
} from "lucide-react";
import Alert from "../ui/Alert";
import Button from "../ui/Button";
import { driveValidationService } from "../../lib/driveValidationService";

const MessageInput = ({
  onSendMessage,
  disabled = false,
  currentUser,
  conversationBrand,
  placeholder = "Type your message or paste a Google Drive link...",
}) => {
  const [message, setMessage] = useState("");
  const [isDriveLink, setIsDriveLink] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const textareaRef = useRef(null);

  // ====================
  // AUTO-RESIZE TEXTAREA
  // ====================

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "inherit";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // ====================
  // DRIVE LINK DETECTION
  // ====================

  useEffect(() => {
    const detectDriveLink = (text) => {
      const drivePatterns = [
        /https:\/\/drive\.google\.com\//,
        /https:\/\/docs\.google\.com\//,
      ];
      return drivePatterns.some((pattern) => pattern.test(text));
    };

    const hasDriveLink = detectDriveLink(message);
    if (hasDriveLink !== isDriveLink) {
      setIsDriveLink(hasDriveLink);
      setValidationError(null);
    }
  }, [message, isDriveLink]);

  // ====================
  // MESSAGE HANDLING
  // ====================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) return;
    if (disabled) return;

    try {
      setIsValidating(true);
      setValidationError(null);

      // Determine message type and extract drive link
      let messageType = "text";
      let driveLink = null;
      let messageText = message.trim();

      if (isDriveLink) {
        // Extract drive link from message
        const driveUrlMatch = messageText.match(
          /(https:\/\/(?:drive|docs)\.google\.com\/[^\s]+)/
        );
        if (driveUrlMatch) {
          driveLink = driveUrlMatch[1];
          messageType = "drive_link";

          // Remove the URL from message text if it's the only content
          const textWithoutUrl = messageText
            .replace(driveUrlMatch[1], "")
            .trim();
          if (!textWithoutUrl) {
            messageText = `Drive submission for ${conversationBrand === "holistic_hercules" ? "Holistic Hercules" : "Aphrodite"}`;
          } else {
            messageText = textWithoutUrl;
          }
        }
      }

      // Send the message
      await onSendMessage(messageText, messageType, driveLink);

      // Clear input on success
      setMessage("");
      setIsDriveLink(false);
      setValidationError(null);
    } catch (error) {
      console.error("❌ Message submission error:", error);
      setValidationError(error.message);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handlePaste = async (e) => {
    const pastedText = e.clipboardData.getData("text");

    // Auto-detect if pasted content is a Google Drive link
    if (
      pastedText.includes("drive.google.com") ||
      pastedText.includes("docs.google.com")
    ) {
      const currentText = message;
      const newText = currentText + pastedText;
      setMessage(newText);
      e.preventDefault();
    }
  };

  // ====================
  // RENDER HELPERS
  // ====================

  const renderValidationError = () => {
    if (!validationError) return null;

    return (
      <div className="mb-3">
        <Alert variant="error">
          <div className="flex items-start space-x-2">
            <span className="text-red-400 mt-0.5">⚠️</span>
            <div>
              <p className="text-sm font-medium text-red-400 mb-1">
                Submission Error
              </p>
              <p className="text-sm text-gray-300">{validationError}</p>
            </div>
          </div>
        </Alert>
      </div>
    );
  };

  // ====================
  // MAIN RENDER
  // ====================

  return (
    <div className="space-y-3">
      {/* Validation Error */}
      {renderValidationError()}

      {/* Drive Link Detection - Minimal version */}
      {isDriveLink && (
        <div className="px-4 py-2 bg-blue-500/10 backdrop-blur-xl rounded-lg border border-blue-500/20">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-blue-400">🔗</span>
            <span className="text-blue-400 font-medium">
              Google Drive submission detected
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-400 text-xs">
              Make sure sharing is set to "Anyone with the link can view"
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Modern Input with Inline Send Button */}
        <div className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <div className="flex items-end space-x-3">
            {/* Text Input */}
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onPaste={handlePaste}
                placeholder={placeholder}
                disabled={disabled || isValidating}
                className="w-full bg-transparent text-white placeholder-gray-400 resize-none border-none outline-none min-h-[24px] max-h-32"
                rows={1}
              />
            </div>

            {/* Inline Send Button */}
            <Button
              type="submit"
              disabled={!message.trim() || disabled || isValidating}
              className={`
                flex-shrink-0 p-2 rounded-lg transition-all duration-200
                ${
                  message.trim() && !disabled && !isValidating
                    ? "bg-[#B8460E] hover:bg-[#8B3A0E] text-white shadow-lg hover:shadow-xl hover:scale-105"
                    : "bg-gray-600 text-gray-400 cursor-not-allowed"
                }
              `}
              title={isDriveLink ? "Submit Drive Link" : "Send Message"}
            >
              {isValidating ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>

          {/* Drive Link Indicator */}
          {isDriveLink && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <div className="flex items-center space-x-2 text-xs text-blue-400">
                <span>📤</span>
                <span>This will be submitted as a creative asset</span>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default MessageInput;
